import { useRef, useState, type DependencyList } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { createLogger } from "@/utils/debug";
import { captureError } from "@/components/error-boundary";

/**
 * Crash-containment wrapper around dexie-react-hooks' `useLiveQuery`.
 *
 * Why this exists
 * ---------------
 * iOS/iPadOS Safari (WebKit) intermittently throws
 *   `UnknownError: Attempt to iterate a cursor that doesn't exist`
 * when an IndexedDB read **cursor** is iterated after WebKit has prematurely
 * auto-committed the read transaction — which happens when the read is
 * interleaved with concurrent writes. `useLiveQuery` re-runs its querier on
 * every relevant write, so an active chat (incoming socket messages) keeps
 * racing cursor scans against writes and trips this bug.
 *
 * `dexie-react-hooks` rethrows querier errors **during render**, so the throw
 * escapes to the nearest route error boundary and the whole chat route crashes
 * into the "Something went wrong" modal. This wrapper contains that specific,
 * known-transient failure so the route never crashes, while deliberately
 * letting every other error through untouched.
 *
 * Why a fresh subscription instead of an in-querier retry
 * -------------------------------------------------------
 * Catching the error inside the querier and re-reading does NOT restore
 * tracking: once a querier execution hits the cursor throw, Dexie's liveQuery
 * commits a dead/empty observed-dependency set, so subsequent writes stop
 * re-firing (the message list silently freezes). This was proven by the
 * integration test in `__tests__/useSafeLiveQuery.test.tsx`. The only reliable
 * recovery is a brand-new `useLiveQuery` subscription, which we trigger by
 * bumping a state nonce that is part of the deps array.
 *
 * Contract (see `.plans/in-progress/fe-idb-cursor-crash/plan.md`)
 * ---------------------------------------------------------------
 * - On success: return the value, remember it as "last good", reset the
 *   re-subscribe budget.
 * - On a **known cursor error only**: serve the **last-good** value (falling
 *   back to the provided default only if there is no last-good yet) so the
 *   route boundary never fires, and schedule a fresh subscription (bounded to
 *   `MAX_RESUBSCRIBES` consecutive attempts so a persistent failure can't
 *   busy-loop). A successful fresh run re-establishes tracking.
 * - Any error that is **not** a known cursor error is re-thrown unchanged, so
 *   genuine faults are still reported. The matcher is intentionally narrow:
 *   broadening it would mask real bugs.
 */

const log = createLogger("SafeLiveQuery");

/** Max consecutive fresh-subscription attempts before we stop (avoid a busy loop). */
const MAX_RESUBSCRIBES = 3;

/**
 * Routine (recovering) suppressions are throttled per-label to this interval
 * before emitting prod telemetry, so a write-storm on iOS can't flood the
 * shared error-report rate-limit budget and crowd out genuine crash reports.
 * The budget-exhausted (frozen-list) case bypasses this — it's rare and the
 * signal we most want to see.
 */
const SUPPRESSION_TELEMETRY_THROTTLE_MS = 30_000;
const lastSuppressionReportAt = new Map<string, number>();

/**
 * Emit non-fatal prod telemetry when a cursor error is suppressed, so we can
 * confirm in production that the fix is firing (and recovering) rather than
 * silently masking a degraded experience. Routed through the existing
 * `captureError` pipeline (dedupe + rate-limit + scrub already applied) but
 * tagged `source: "suppressed"` so it's filterable from real crashes. Contains
 * only the static query label + error name — never query data.
 */
const reportSuppression = (
  label: string,
  servedLastGood: boolean,
  budgetExhausted: boolean,
  original: unknown,
): void => {
  if (!budgetExhausted) {
    const now = Date.now();
    const last = lastSuppressionReportAt.get(label);
    if (last !== undefined && now - last < SUPPRESSION_TELEMETRY_THROTTLE_MS) {
      return;
    }
    lastSuppressionReportAt.set(label, now);
  }

  try {
    const originName = (original as { name?: string } | null)?.name ?? "Error";
    const detail = `[query=${label}, origin=${originName}, servedLastGood=${servedLastGood}]`;
    const err = new Error(
      budgetExhausted
        ? `[suppressed] IndexedDB cursor error — recovery budget exhausted; list may be stale until navigation ${detail}`
        : `[suppressed] IndexedDB cursor error — recovering via fresh subscription ${detail}`,
    );
    err.name = "SuppressedCursorError";
    captureError({ error: err, scope: "route", source: "suppressed" });
  } catch {
    // Telemetry must never become a second error in the suppression path.
  }
};

/**
 * Collect the human-readable text of an error, including a Dexie-wrapped
 * `inner` cause, so the matcher works whether the throw is a raw DOMException
 * or a Dexie error wrapping one.
 */
const errorText = (error: unknown): string => {
  const e = error as
    | {
        name?: string;
        message?: string;
        inner?: { name?: string; message?: string };
      }
    | null
    | undefined;
  if (!e) {
    return "";
  }
  return [e.name, e.message, e.inner?.name, e.inner?.message]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

/**
 * True only for the known WebKit IndexedDB cursor-iteration failure. Matches on
 * the exact message phrasing observed in prod telemetry (not error name), so
 * generic `UnknownError` / `InvalidStateError` / `TransactionInactiveError`
 * thrown for any other reason (DB close/clear/upgrade, real cursor-misuse bugs)
 * are NOT swallowed. New WebKit variants should only be added here once their
 * exact text has been observed and a test added.
 */
export const isTransientCursorError = (error: unknown): boolean => {
  const text = errorText(error);
  return (
    // "Attempt to iterate a cursor that doesn't exist" — the prod signature.
    text.includes("cursor that doesn't exist") ||
    text.includes("iterate a cursor") ||
    // Sibling WebKit family seen in prod telemetry: an IndexedDB op (opening a
    // cursor, or a bulkPut's internal range-delete) running after the read
    // transaction was eagerly auto-committed under a write storm. Same transient
    // class, so contain it identically instead of crashing the route via the
    // error boundary. Matched on the EXACT observed phrases (not loose
    // substrings) so an unrelated transaction-misuse error — even one that
    // happens to mention a cursor — is NOT swallowed.
    text.includes(
      "open a cursor in database without an in-progress transaction",
    ) ||
    text.includes(
      "delete range from database without an in-progress transaction",
    )
  );
};

const describeError = (error: unknown): { name?: string; message?: string } => {
  const e = error as { name?: string; message?: string } | null | undefined;
  return { name: e?.name, message: e?.message };
};

/**
 * Pure querier wrapper (no React) so it can be unit-tested directly. Returns
 * the read result on success; on a known cursor error it serves the last-good
 * value and invokes `onTransientError` (the hook uses that to re-subscribe);
 * non-cursor errors propagate.
 */
export const runSafeQuerier = async <T>(
  querier: () => Promise<T> | T,
  lastGoodRef: { current: T | undefined },
  opts: {
    label?: string;
    onSuccess?: () => void;
    onTransientError?: (error: unknown) => void;
  } = {},
): Promise<T | undefined> => {
  const { label = "query", onSuccess, onTransientError } = opts;
  try {
    const result = await querier();
    lastGoodRef.current = result;
    onSuccess?.();
    return result;
  } catch (error) {
    if (!isTransientCursorError(error)) {
      // Not the known-transient cursor bug — let real faults surface.
      throw error;
    }
    log.warn("Suppressed IndexedDB cursor error; re-subscribing", {
      label,
      error: describeError(error),
      servedLastGood: lastGoodRef.current !== undefined,
    });
    onTransientError?.(error);
    return lastGoodRef.current;
  }
};

/**
 * Drop-in replacement for `useLiveQuery` for IndexedDB-backed reads that can
 * hit the WebKit cursor bug. Same call shape, plus an optional `label` used in
 * suppression logs.
 */
export function useSafeLiveQuery<T>(
  querier: () => Promise<T> | T,
  deps?: DependencyList,
): T | undefined;
export function useSafeLiveQuery<T, TDefault>(
  querier: () => Promise<T> | T,
  deps: DependencyList,
  defaultResult: TDefault,
  label?: string,
): T | TDefault;
export function useSafeLiveQuery<T, TDefault>(
  querier: () => Promise<T> | T,
  deps: DependencyList = [],
  defaultResult?: TDefault,
  label = "query",
): T | TDefault | undefined {
  // Last successful value for THIS hook instance, preferred over the static
  // default when serving a fallback (avoids empty-list flicker under load).
  const lastGood = useRef<T | undefined>(defaultResult as T | undefined);
  // Bumping this nonce (part of deps) forces useLiveQuery to drop the dead
  // observable and create a fresh one with working dependency tracking.
  const [resubscribeNonce, setResubscribeNonce] = useState(0);
  const resubscribeBudget = useRef(MAX_RESUBSCRIBES);

  // When the caller's deps change, this hook is now serving a DIFFERENT logical
  // query (e.g. a different chat/thread/link). Reset last-good + budget
  // synchronously during render — before the new querier can run — so that a
  // cursor error on the first read of the new deps never serves the previous
  // query's data (which in a messenger would mean showing another chat's
  // messages). A render-phase ref write is safe here (no setState, no loop).
  const prevDeps = useRef<DependencyList | undefined>(undefined);
  const depsChanged =
    prevDeps.current === undefined ||
    prevDeps.current.length !== deps.length ||
    deps.some((d, i) => !Object.is(d, prevDeps.current![i]));
  if (depsChanged) {
    prevDeps.current = deps;
    lastGood.current = defaultResult as T | undefined;
    resubscribeBudget.current = MAX_RESUBSCRIBES;
  }

  return useLiveQuery(
    () =>
      runSafeQuerier(querier, lastGood, {
        label,
        onSuccess: () => {
          resubscribeBudget.current = MAX_RESUBSCRIBES;
        },
        onTransientError: (original) => {
          // Budget > 0 → spend one attempt on a fresh subscription (recovery).
          // Budget exhausted → stop re-subscribing (the rare frozen-list edge);
          // serve last-good and surface that in telemetry.
          const willResubscribe = resubscribeBudget.current > 0;
          if (willResubscribe) {
            resubscribeBudget.current -= 1;
            // Defer the state update out of the querier's microtask.
            queueMicrotask(() => setResubscribeNonce((n) => n + 1));
          }
          reportSuppression(
            label,
            lastGood.current !== undefined,
            !willResubscribe,
            original,
          );
        },
      }),
    [...deps, resubscribeNonce],
    defaultResult as TDefault,
  ) as T | TDefault | undefined;
}
